import React, { useState, useEffect } from "react";
import accessURL from "../../../../components/utils/superAccess";
import {
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Info,
  ExternalLink,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { FilterState, PaginationInfo, PaymentData } from "./types";
import DeletePayment from "./DeletePayment";

const Payments: React.FC = () => {
  const [data, setData] = useState<PaymentData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(12);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [filters, setFilters] = useState<FilterState>({
    startDate: "",
    endDate: "",
    companyName: "",
    transaction_id: "",
  });
  const [selectedPayment, setSelectedPayment] = useState<PaymentData | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const fetchPayments = async (page: number, pageSize: number) => {
    setLoading(true);
    const params: Record<string, string> = {};

    if (filters.startDate) params.start_date = filters.startDate;
    if (filters.endDate) params.end_date = filters.endDate;
    if (filters.companyName) params.companyName = filters.companyName;
    if (filters.transaction_id) params.transaction_id = filters.transaction_id;

    const queryParams = new URLSearchParams(params);
    let url = `super-admin/getPayments?page=${page}&limit=${pageSize}${
      queryParams.toString() ? `&${queryParams.toString()}` : ""
    }`;

    try {
      const response = await accessURL.get<{
        code: number;
        data: {
          data: PaymentData[];
          pagination: PaginationInfo;
        };
      }>(url);

      if (response.data.code === 200) {
        const processedData = response.data.data.data.map((payment, index) => ({
          ...payment,
          displayId: (page - 1) * pageSize + index + 1,
        }));
        setData(processedData);
        setTotalRows(response.data.data.pagination.total_items);
        setTotalPages(response.data.data.pagination.total_pages);
      } else {
        console.error("Error in API response:", response.data);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPayments(1, perPage);
  };

  const clearFilters = () => {
    setFilters({
      startDate: "",
      endDate: "",
      companyName: "",
      transaction_id: "",
    });
    setCurrentPage(1);
    fetchPayments(1, perPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const showPaymentDetails = (payment: PaymentData) => {
    setSelectedPayment(payment);
    setModalOpen(true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  useEffect(() => {
    fetchPayments(currentPage, perPage);
  }, [currentPage, perPage]);

  return (
    <div className="container mx-auto px-6 py-10 bg-gradient-to-b from-indigo-50 to-white min-h-screen">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center text-indigo-800 bg-white py-4 rounded-lg shadow-sm">
          Payment Dashboard
        </h1>

        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 text-purple-700">
            Search Filters
          </h2>
          <form onSubmit={handleFilterSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="relative group">
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={filters.companyName}
                  onChange={handleFilterChange}
                  placeholder="Company Name"
                  className="w-full pl-10 pr-4 py-3 border border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-indigo-50 text-indigo-900"
                />
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400 group-hover:text-indigo-600 transition-colors"
                  size={18}
                />
              </div>

              <div className="relative group">
                <input
                  type="text"
                  id="transaction_id"
                  name="transaction_id"
                  value={filters.transaction_id}
                  onChange={handleFilterChange}
                  placeholder="Transaction ID"
                  className="w-full pl-10 pr-4 py-3 border border-pink-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 bg-pink-50 text-pink-900"
                />
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-pink-400 group-hover:text-pink-600 transition-colors"
                  size={18}
                />
              </div>

              <div className="relative group">
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  className="w-full pl-10 pr-4 py-3 border border-teal-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 bg-teal-50 text-teal-900"
                />
                <Calendar
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-teal-400 group-hover:text-teal-600 transition-colors"
                  size={18}
                />
              </div>

              <div className="relative group">
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  className="w-full pl-10 pr-4 py-3 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-amber-50 text-amber-900"
                />
                <Calendar
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-400 group-hover:text-amber-600 transition-colors"
                  size={18}
                />
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 font-medium transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                {loading ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <Search size={18} />
                )}
                <span>{loading ? "Searching..." : "Search"}</span>
              </button>

              <button
                type="button"
                onClick={clearFilters}
                className="px-6 py-3 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-lg hover:from-rose-500 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 font-medium transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <XCircle size={18} />
                <span>Clear Filters</span>
              </button>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
            <div className="mb-4 sm:mb-0 flex items-center gap-3">
              <label htmlFor="perPage" className="text-gray-700 font-medium">
                Rows per page:
              </label>
              <select
                id="perPage"
                value={perPage}
                onChange={handlePerPageChange}
                className="border border-indigo-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-indigo-50 text-indigo-900"
              >
                <option value={10}>10</option>
                <option value={12}>12</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>

            <div className="flex items-center bg-gray-100 rounded-lg px-4 py-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-2 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 text-indigo-700 transition-colors"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="mx-4 font-medium text-gray-700">
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={
                  currentPage === totalPages || loading || totalPages === 0
                }
                className="p-2 rounded-md hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 text-indigo-700 transition-colors"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-indigo-600"></div>
              <p className="mt-4 text-indigo-600 font-medium">
                Loading payments data...
              </p>
            </div>
          )}

          {!loading && (
            <div className="bg-white rounded-lg overflow-hidden border border-gray-200">
              <div className="overflow-x-auto">
                <table className="w-full min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr className="bg-gradient-to-r from-purple-100 to-indigo-100">
                      {/* <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider"
                      >
                        ID
                      </th> */}
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider"
                      >
                        Company
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider"
                      >
                        Transaction ID
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider"
                      >
                        Amount
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider"
                      >
                        Package
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider"
                      >
                        Created At
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-4 text-left text-xs font-medium text-indigo-800 uppercase tracking-wider"
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.length > 0 ? (
                      data.map((payment) => (
                        <tr
                          key={payment.id}
                          className="hover:bg-indigo-50 transition-colors"
                        >
                          {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {payment.id}
                          </td> */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <div className="truncate max-w-xs font-medium">
                              {payment.companyName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            <div className="truncate max-w-xs">
                              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md font-mono text-xs">
                                {payment.transaction_id}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm font-semibold text-green-600">
                              USD{payment.amount}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full">
                              {payment.payment_package}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                            {payment.created_At}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex items-center space-x-10">
                            <button
                              onClick={() => showPaymentDetails(payment)}
                              className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1 bg-indigo-50 hover:bg-indigo-100 px-3 py-2 rounded-md transition-colors"
                            >
                              <Info size={16} />
                              <span>Details</span>
                            </button>
                            <DeletePayment paymentID={payment.id} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center justify-center">
                            <XCircle className="h-12 w-12 text-gray-400 mb-4" />
                            <p className="text-gray-500 text-lg">
                              No payments found
                            </p>
                            <p className="text-gray-400 text-sm mt-2">
                              Try adjusting your filters
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {modalOpen && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto transform transition-all animate-fade-in">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-indigo-800">
                  Payment Details
                </h3>
                <button
                  onClick={() => setModalOpen(false)}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl shadow-inner">
                  <h4 className="font-semibold text-indigo-800 text-lg mb-3 flex items-center">
                    <span className="bg-indigo-100 p-2 rounded-full mr-2">
                      <ExternalLink size={16} className="text-indigo-600" />
                    </span>
                    Transaction Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white bg-opacity-50 p-3 rounded-lg">
                      <p className="text-sm text-indigo-500">Transaction ID</p>
                      <p className="font-medium font-mono text-gray-800">
                        {selectedPayment.transaction_id}
                      </p>
                    </div>
                    <div className="bg-white bg-opacity-50 p-3 rounded-lg">
                      <p className="text-sm text-indigo-500">Date</p>
                      <p className="font-medium text-gray-800">
                        {selectedPayment.created_At}
                      </p>
                    </div>
                    <div className="bg-white bg-opacity-50 p-3 rounded-lg">
                      <p className="text-sm text-indigo-500">Amount</p>
                      <p className="font-semibold text-green-600 text-lg">
                        USD{selectedPayment.amount}
                      </p>
                    </div>
                    <div className="bg-white bg-opacity-50 p-3 rounded-lg">
                      <p className="text-sm text-indigo-500">Package</p>
                      <p className="font-medium text-gray-800">
                        <span className="bg-indigo-100 px-2 py-1 rounded-md">
                          {selectedPayment.payment_package}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-gray-50 to-slate-100 p-5 rounded-xl shadow-inner">
                  <h4 className="font-semibold text-gray-800 text-lg mb-3 flex items-center">
                    <span className="bg-gray-200 p-2 rounded-full mr-2">
                      <Info size={16} className="text-gray-600" />
                    </span>
                    Company Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white bg-opacity-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Company Name</p>
                      <p className="font-medium text-gray-800">
                        {selectedPayment.companyName}
                      </p>
                    </div>
                    <div className="bg-white bg-opacity-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-500">Company ID</p>
                      <p className="font-medium font-mono text-gray-800">
                        {selectedPayment.company_id}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-xl shadow-inner">
                  <h4 className="font-semibold text-purple-800 text-lg mb-3 flex items-center">
                    <span className="bg-purple-100 p-2 rounded-full mr-2">
                      <Calendar size={16} className="text-purple-600" />
                    </span>
                    Subscription Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white bg-opacity-50 p-3 rounded-lg">
                      <p className="text-sm text-purple-500">Expiry Date</p>
                      <p className="font-medium text-gray-800">
                        {formatDate(selectedPayment.expiry_date)}
                      </p>
                    </div>
                    <div className="bg-white bg-opacity-50 p-3 rounded-lg">
                      <p className="text-sm text-purple-500">Payment ID</p>
                      <p className="font-medium text-gray-800">
                        <span className="bg-purple-100 px-2 py-1 rounded-md font-mono">
                          #{selectedPayment.id}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end gap-4">
                <button
                  onClick={() => setModalOpen(false)}
                  className="px-5 py-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg text-gray-700 hover:from-gray-300 hover:to-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 font-medium transition-all shadow-sm hover:shadow-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
